<script lang="ts">
  /**
   * Multi-series line chart in the "Pulse" visual style — bezier-smoothed
   * curves, gradient fill, glow points, theme colors, DPR-crisp canvas,
   * and hover interactivity (nearest-point readout + tooltip).
   * Adapted from ScoreChart.svelte for arbitrary count-series.
   */
  interface Series { label: string; color: string; points: number[]; }
  interface Props { series: Series[]; xLabels: string[]; }
  let { series, xLabels }: Props = $props();

  let canvasEl: HTMLCanvasElement | undefined = $state();
  let hover = $state<{ si: number; pi: number; x: number; y: number } | null>(null);

  const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const PAD = { t: 14, r: 14, b: 26, l: 28 };

  $effect(() => {
    series; xLabels; hover;
    if (canvasEl) requestAnimationFrame(draw);
  });

  // Redraw on container resize. Without this, the canvas bitmap keeps its
  // old internal pixel dimensions while CSS stretches the element to fit
  // the new container width — the browser scales the bitmap and text gets
  // visibly stretched. ResizeObserver triggers a fresh draw at the new
  // size so text stays crisp at any window width.
  $effect(() => {
    if (!canvasEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => requestAnimationFrame(draw));
    ro.observe(canvasEl);
    return () => ro.disconnect();
  });

  interface Pt { x: number; y: number; v: number; si: number; pi: number; }

  function layout(w: number, h: number) {
    const plotW = w - PAD.l - PAD.r;
    const plotH = h - PAD.t - PAD.b;
    const n = xLabels.length;
    let maxVal = 4;
    for (const s of series) for (const p of s.points) maxVal = Math.max(maxVal, p);
    const yFor = (v: number) => PAD.t + plotH - (v / maxVal) * plotH;
    const xFor = (i: number) => PAD.l + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2);
    const all: Pt[] = [];
    series.forEach((s, si) => s.points.forEach((v, pi) => {
      all.push({ x: xFor(pi), y: yFor(v), v, si, pi });
    }));
    return { plotW, plotH, n, maxVal, yFor, xFor, all };
  }

  function draw() {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const w = rect.width, h = rect.height;
    if (w === 0 || h === 0) return;
    canvasEl.width = w * DPR;
    canvasEl.height = h * DPR;
    const ctx = canvasEl.getContext('2d')!;
    ctx.scale(DPR, DPR);
    ctx.clearRect(0, 0, w, h);

    const cs = getComputedStyle(canvasEl);
    const gridColor = cs.getPropertyValue('--border').trim() || '#e5e7eb';
    const labelColor = cs.getPropertyValue('--text-dim').trim() || '#9ca3af';
    const surface = cs.getPropertyValue('--bg-surface').trim() || '#fff';

    const { plotH, n, maxVal, yFor, xFor } = layout(w, h);

    // Y gridlines
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'right';
    for (let t = 0; t <= 4; t++) {
      const v = (maxVal / 4) * t;
      const y = yFor(v);
      ctx.strokeStyle = gridColor; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(w - PAD.r, y); ctx.stroke();
      ctx.fillStyle = labelColor;
      ctx.fillText(String(Math.round(v)), PAD.l - 6, y + 3);
    }

    // X labels
    ctx.textAlign = 'center';
    ctx.fillStyle = labelColor;
    for (let i = 0; i < n; i++) ctx.fillText(xLabels[i], xFor(i), h - 7);

    // Hover guide line
    if (hover) {
      const hx = xFor(hover.pi);
      ctx.save();
      ctx.strokeStyle = gridColor; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(hx, PAD.t); ctx.lineTo(hx, PAD.t + plotH); ctx.stroke();
      ctx.restore();
    }

    const baseY = PAD.t + plotH;
    series.forEach((s, si) => {
      const pts = s.points.map((v, i) => ({ x: xFor(i), y: yFor(v) }));
      if (pts.length === 0) return;

      if (pts.length > 1) {
        const grad = ctx.createLinearGradient(0, PAD.t, 0, baseY);
        grad.addColorStop(0, s.color + '24');
        grad.addColorStop(1, s.color + '03');
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(0, i - 1)], p1 = pts[i];
          const p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
          const cp1x = p1.x + (p2.x - p0.x) / 6, cp1y = p1.y + (p2.y - p0.y) / 6;
          const cp2x = p2.x - (p3.x - p1.x) / 6, cp2y = p2.y - (p3.y - p1.y) / 6;
          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
        }
        ctx.lineTo(pts[pts.length - 1].x, baseY);
        ctx.lineTo(pts[0].x, baseY);
        ctx.closePath();
        ctx.fillStyle = grad; ctx.fill();

        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(0, i - 1)], p1 = pts[i];
          const p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
          const cp1x = p1.x + (p2.x - p0.x) / 6, cp1y = p1.y + (p2.y - p0.y) / 6;
          const cp2x = p2.x - (p3.x - p1.x) / 6, cp2y = p2.y - (p3.y - p1.y) / 6;
          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
        }
        ctx.strokeStyle = s.color; ctx.lineWidth = 2.5;
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.stroke();
      }

      pts.forEach((p, pi) => {
        const isHover = hover && hover.si === si && hover.pi === pi;
        const r = isHover ? 5 : 3;
        ctx.beginPath(); ctx.arc(p.x, p.y, r + 3, 0, Math.PI * 2);
        ctx.fillStyle = s.color + (isHover ? '40' : '22'); ctx.fill();
        ctx.beginPath(); ctx.arc(p.x, p.y, r + 1, 0, Math.PI * 2);
        ctx.fillStyle = surface; ctx.fill();
        ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = s.color; ctx.fill();
      });
    });
  }

  function onMove(e: MouseEvent) {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const { all } = layout(rect.width, rect.height);
    let best: Pt | null = null, bestD = 22;
    for (const p of all) {
      const d = Math.hypot(mx - p.x, my - p.y);
      if (d < bestD) { bestD = d; best = p; }
    }
    hover = best ? { si: best.si, pi: best.pi, x: best.x, y: best.y } : null;
  }
  function onLeave() { hover = null; }
</script>

<div class="trc-wrap">
  <canvas bind:this={canvasEl} onmousemove={onMove} onmouseleave={onLeave}></canvas>
  {#if hover}
    <div class="trc-tip" style="left:{hover.x}px; top:{hover.y}px;">
      <span class="trc-tip-dot" style="background:{series[hover.si].color};"></span>
      <span class="trc-tip-name">{series[hover.si].label}</span>
      <span class="trc-tip-val">{series[hover.si].points[hover.pi]}</span>
      <span class="trc-tip-x">{xLabels[hover.pi]}</span>
    </div>
  {/if}
</div>

<style>
  .trc-wrap { height: 150px; width: 100%; position: relative; }
  .trc-wrap canvas { width: 100%; height: 100%; display: block; cursor: crosshair; }
  .trc-tip {
    position: absolute;
    transform: translate(-50%, -130%);
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.18);
    z-index: 5;
  }
  .trc-tip-dot { width: 7px; height: 7px; border-radius: 2px; flex-shrink: 0; }
  .trc-tip-name { color: var(--text-muted); }
  .trc-tip-val { color: var(--text); font-weight: 700; font-family: var(--font-mono); }
  .trc-tip-x { color: var(--text-dim); }
</style>
